// API Types for HoneyDo Platform
export type UserType = 'parent' | 'spouse' | 'senior' | 'roommate';
export type AgeRange = 'adult';
export type TaskStatus = 'pending' | 'in_progress' | 'completed' | 'approved';
export type TaskCategory = 'plumbing' | 'electrical' | 'painting' | 'carpentry' | 'landscaping' | 'general' | 'household' | 'errands' | 'childcare' | 'petcare' | 'financial' | 'health' | 'home_maintenance' | 'special_event' | 'exterior' | 'yard' | 'outdoor' | 'maintenance' | 'other';
export type TaskDifficulty = 'easy' | 'medium' | 'hard';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

// Enhanced priority classification system with visual indicators
export type EnhancedTaskPriority = 'standard' | 'high' | 'urgent';

export interface TaskPriorityMetadata {
  level: TaskPriority;
  emoji: string;
  description: string;
  weight: number;
  autoEscalation?: boolean;
}

export const TASK_PRIORITY_CONFIG: Record<TaskPriority, TaskPriorityMetadata> = {
  'urgent': {
    level: 'urgent',
    emoji: '🔥',
    description: 'Revenue blockers, legal compliance, launch critical',
    weight: 100,
    autoEscalation: true
  },
  'high': {
    level: 'high', 
    emoji: '⚠️',
    description: 'Growth acceleration, partnership enablers, platform improvements',
    weight: 75,
    autoEscalation: false
  },
  'medium': {
    level: 'medium',
    emoji: '📋', 
    description: 'Standard feature development, optimization, user experience',
    weight: 50,
    autoEscalation: false
  },
  'low': {
    level: 'low',
    emoji: '📝',
    description: 'Nice-to-have features, future planning, research',
    weight: 25,
    autoEscalation: false
  }
};

export interface User {
  user_id: string;
  email: string;
  display_name: string;
  user_type: UserType;
  age_range: AgeRange;
  family_id: string;
  permissions: UserPermissions;
  is_active: boolean;
  created_at: string;
  last_active: string;
  tasks_created_count: number;
  tasks_completed_count: number;
  points_earned: number;
}

export interface UserPermissions {
  create_tasks: boolean;
  assign_tasks: boolean;
  complete_tasks: boolean;
  view_analytics: boolean;
  manage_family: boolean;
}

export interface Family {
  family_id: string;
  family_name: string;
  created_by: string;
  max_members: number;
  max_tasks: number;
  created_at: string;
  total_tasks_count: number;
  completed_tasks_count: number;
  active_members_count: number;
  member_count?: number;
  subscription_tier?: 'free' | 'premium' | 'pro';
}

export interface Task {
  id: string;
  task_id?: string; // Backwards compatibility
  family_id: string;
  title: string;
  description?: string;
  category: TaskCategory;
  difficulty?: TaskDifficulty;
  priority?: TaskPriority;
  created_by: string;
  assigned_to?: string | string[];
  completed_by?: string;
  status: TaskStatus;
  is_urgent?: boolean;
  priority_score: number;
  due_date?: string;
  estimated_hours?: number;
  estimated_duration?: number;
  emotional_effort?: number;
  requires_purchase?: boolean;
  estimated_cost?: number;
  can_outsource?: boolean;
  outsource_reason?: string;
  actual_hours?: number;
  location?: string;
  tags?: string[];
  photos?: string[];
  completion_notes?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  
  // Enhanced priority and revenue tracking fields
  priority_metadata?: TaskPriorityMetadata;
  revenue_impact?: {
    blocks_revenue?: boolean;
    estimated_value_unlock?: number; // In cents
    family_satisfaction_impact?: number; // 1-10 scale
    agent_escalation_triggered?: boolean;
    cross_project_priority_context?: {
      relative_priority?: number; // 1-100 scale across all family projects
      resource_conflicts?: string[];
      delegation_opportunity?: boolean;
    };
  };
  
  // Agent coordination metadata
  agent_involvement?: {
    assigned_agents?: string[];
    escalation_history?: Array<{
      agent: string;
      timestamp: string;
      reason: string;
      action_taken: string;
    }>;
    coordination_status?: 'none' | 'pending' | 'active' | 'completed';
  };
}

// HoneyDo API Response format (following Tim-Combo patterns)
export interface HoneyDoApiResponse<T = any> {
  success: true;
  message: string;
  meta: {
    Total_Records: number;
    Request_ID: string;
    Timestamp: string;
    Family_ID?: string;
    User_ID?: string;
  };
  data: {
    Records: T[];
    Query_Context?: {
      Operation?: string;
      Family_ID?: string;
      User_ID?: string;
      Is_New_Family?: boolean;
    };
    Analytics?: {
      Total_Active: number;
    };
  };
}

export interface HoneyDoApiErrorResponse {
  success: false;
  message: string;
  meta: {
    Timestamp: string;
    Request_ID?: string;
    Error_Source: string;
  };
  error_code: string;
  error_details?: string;
  debug?: any;
}

export type HoneyDoApiResponseType<T = any> = HoneyDoApiResponse<T> | HoneyDoApiErrorResponse;

// Type guards
export function isSuccessResponse<T>(response: HoneyDoApiResponseType<T>): response is HoneyDoApiResponse<T> {
  return response.success === true;
}

export function isErrorResponse(response: HoneyDoApiResponseType): response is HoneyDoApiErrorResponse {
  return response.success === false;
}

// Legacy support (kept for backward compatibility)
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  metadata?: {
    total_records: number;
    request_id: string;
    query_context?: any;
  };
}

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  userId: string;
}

// Enhanced Recurring Task & Maintenance Types
export type RecurrencePattern = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually' | 'custom' | 'seasonal' | 'weather-dependent';
export type Season = 'spring' | 'summer' | 'fall' | 'winter';
export type WeatherCondition = 'sunny' | 'clear' | 'cloudy' | 'foggy' | 'rainy' | 'snow' | 'stormy' | 'cold' | 'hot' | 'dry' | 'humid' | 'windy';

export interface WeatherTrigger {
  condition: WeatherCondition;
  threshold?: number; // Temperature, humidity %, etc.
  duration?: number; // Days of condition
  operator: 'above' | 'below' | 'equals' | 'after' | 'before';
}

export interface SeasonalTrigger {
  season: Season;
  timing: 'early' | 'mid' | 'late' | 'first-warm-day' | 'last-frost' | 'first-frost';
  temperature?: number; // Optional temperature threshold
}

export interface CustomInterval {
  value: number;
  unit: 'days' | 'weeks' | 'months';
}

export interface SkipCondition {
  weatherConditions?: WeatherCondition[];
  temperature?: { min?: number; max?: number };
  forecastDays?: number; // How many days ahead to check
  reason?: string;
}

export interface RecurrenceRule {
  pattern: RecurrencePattern;
  interval: number;
  
  // Standard patterns
  dayOfWeek?: number; // 0-6 (Sunday-Saturday)
  dayOfMonth?: number; // 1-31
  
  // Custom intervals
  customInterval?: CustomInterval;
  
  // Seasonal patterns
  seasonalTrigger?: SeasonalTrigger;
  
  // Weather-dependent patterns
  weatherTrigger?: WeatherTrigger;
  
  // Skip conditions
  skipConditions?: SkipCondition[];
  
  // End conditions
  endDate?: string;
  maxOccurrences?: number;
}

export interface MaintenanceSchedule {
  scheduleId: string;
  schedule_id?: string; // Backend compatibility  
  familyId: string;
  family_id?: string; // Backend compatibility
  title: string;
  description?: string;
  category: TaskCategory;
  subcategory?: string; // e.g., 'pest-control', 'hvac-maintenance', 'garden-care'
  
  // Recurrence configuration
  recurrence: RecurrenceRule;
  
  // Assignment and cost
  assignedTo?: string;
  estimatedCost?: number;
  estimatedHours?: number;
  
  // Scheduling
  nextOccurrence: string;
  lastCompleted?: string;
  completionHistory?: Array<{
    date: string;
    completedBy: string;
    notes?: string;
    cost?: number;
    skipReason?: string;
  }>;
  
  // Smart features
  weatherDependent?: boolean;
  contractorRecommended?: boolean;
  supplyTracking?: {
    itemName: string;
    currentStock: number;
    reorderThreshold: number;
    estimatedUsagePerTask: number;
  };
  
  // Location and conditions
  location?: string;
  zipCode?: string; // For weather integration
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  tags?: string[];
}

// Household-specific task categories
export interface HouseholdTaskCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  subcategories: Array<{
    id: string;
    name: string;
    weatherDependent?: boolean;
    seasonal?: boolean;
    contractorRecommended?: boolean;
  }>;
}

// Type transformation utilities for API compatibility (EquilateralAgents pattern)
export function normalizeMaintenanceSchedule(schedule: any): MaintenanceSchedule {
  return {
    ...schedule,
    scheduleId: schedule.scheduleId || schedule.schedule_id,
    familyId: schedule.familyId || schedule.family_id,
    // Ensure both formats are available for backward compatibility
    schedule_id: schedule.schedule_id || schedule.scheduleId,
    family_id: schedule.family_id || schedule.familyId
  } as MaintenanceSchedule;
}

export function serializeMaintenanceScheduleForAPI(schedule: Partial<MaintenanceSchedule>): any {
  const serialized = { ...schedule };
  // Backend expects snake_case
  if (schedule.scheduleId && !serialized.schedule_id) {
    serialized.schedule_id = schedule.scheduleId;
  }
  if (schedule.familyId && !serialized.family_id) {
    serialized.family_id = schedule.familyId;
  }
  return serialized;
}

export const HOUSEHOLD_TASK_CATEGORIES: HouseholdTaskCategory[] = [
  {
    id: 'pest-control',
    name: 'Pest Control',
    icon: '🐛',
    description: 'Preventive and treatment pest management',
    subcategories: [
      { id: 'ant-treatment', name: 'Ant Treatment', weatherDependent: true, seasonal: true },
      { id: 'termite-inspection', name: 'Termite Inspection', contractorRecommended: true },
      { id: 'rodent-control', name: 'Rodent Control', seasonal: true },
      { id: 'general-spraying', name: 'General Pest Spraying', weatherDependent: true }
    ]
  },
  {
    id: 'garden-landscaping',
    name: 'Garden & Landscaping',
    icon: '🌱',
    description: 'Garden care and outdoor maintenance',
    subcategories: [
      { id: 'lawn-care', name: 'Lawn Care', seasonal: true, weatherDependent: true },
      { id: 'watering', name: 'Garden Watering', weatherDependent: true },
      { id: 'pruning', name: 'Plant Pruning', seasonal: true },
      { id: 'fertilizing', name: 'Fertilizing', seasonal: true },
      { id: 'weed-control', name: 'Weed Control', weatherDependent: true }
    ]
  },
  {
    id: 'hvac-maintenance',
    name: 'HVAC Maintenance',
    icon: '🌬️',
    description: 'Heating, ventilation, and air conditioning',
    subcategories: [
      { id: 'filter-replacement', name: 'Filter Replacement' },
      { id: 'system-service', name: 'Professional Service', contractorRecommended: true, seasonal: true },
      { id: 'duct-cleaning', name: 'Duct Cleaning', contractorRecommended: true },
      { id: 'thermostat-maintenance', name: 'Thermostat Maintenance', seasonal: true }
    ]
  },
  {
    id: 'seasonal-maintenance',
    name: 'Seasonal Maintenance',
    icon: '🏠',
    description: 'Seasonal home preparation and maintenance',
    subcategories: [
      { id: 'winterization', name: 'Winterization', seasonal: true },
      { id: 'gutter-cleaning', name: 'Gutter Cleaning', seasonal: true, weatherDependent: true },
      { id: 'deck-maintenance', name: 'Deck Maintenance', seasonal: true, weatherDependent: true },
      { id: 'window-washing', name: 'Window Washing', weatherDependent: true }
    ]
  },
  {
    id: 'appliance-care',
    name: 'Appliance Care',
    icon: '🔧',
    description: 'Regular appliance maintenance and service',
    subcategories: [
      { id: 'deep-clean', name: 'Deep Cleaning' },
      { id: 'filter-maintenance', name: 'Filter Maintenance' },
      { id: 'professional-service', name: 'Professional Service', contractorRecommended: true },
      { id: 'calibration', name: 'Calibration & Testing' }
    ]
  }
];